/**
 * /account — the signed-in user's own settings, as opposed to an org's.
 *
 * Lives at the top level rather than under /o/:slug/settings because none of
 * it is org-scoped: the same password applies whichever org you are looking
 * at, so filing it under one org's URL would imply a scope it doesn't have.
 *
 * Today it holds one section, Password. The layout is deliberately a list of
 * sections so email, name and connected accounts can join it without a
 * redesign.
 */
import React, { FormEvent, useEffect, useState } from 'react';

import AuthRoute from '@/app/routing/guards/auth-route/AuthRoute';
import AppShell from '@/features/dashboard/components/app-shell/AppShell';

import '@/features/auth/components/auth.styles.css';

const MIN_LENGTH = 8;

function PasswordSection() {
  /** null until we know; true once the account is known to have a password. */
  const [hasPassword, setHasPassword] = useState<boolean | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    // Its own endpoint rather than /me, which is a JWT-only fast path that
    // every guarded page mount already calls.
    fetch('/api/auth/password', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { hasPassword?: boolean } | null) => {
        if (!cancelled) setHasPassword(d?.hasPassword ?? null);
      })
      .catch(() => {
        /* fall back to the neutral wording */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setDone(null);

    if (password.length < MIN_LENGTH) {
      setError(`Password must be at least ${MIN_LENGTH} characters.`);
      return;
    }
    // Caught here rather than server-side: a typo you can't see is the main
    // way a single-field password form goes wrong.
    if (password !== confirm) {
      setError('The two passwords don’t match.');
      return;
    }

    setBusy(true);
    try {
      const res = await fetch('/api/auth/password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ password }),
      });
      const data = (await res.json().catch(() => ({}))) as Record<string, unknown>;
      if (!res.ok) {
        setError((data.error as string) || 'Could not update your password.');
        return;
      }
      setPassword('');
      setConfirm('');
      setHasPassword(true);
      setDone(
        data.created ? 'Password set. You can now sign in with it too.' : 'Password changed.',
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setBusy(false);
    }
  };

  const verb = hasPassword === false ? 'Set a password' : 'Change password';
  const cta = hasPassword === false ? 'Set password' : 'Change password';

  return (
    <section className="tile-auth-host account-section">
      <h2 className="account-section-title">{verb}</h2>
      <p className="account-section-help">
        {hasPassword === false
          ? 'You signed in with Google. Adding a password lets you sign in either way.'
          : 'Enter a new password. You won’t need your current one.'}
      </p>

      {error ? <div className="auth-banner auth-banner-error">{error}</div> : null}
      {done ? <div className="auth-banner auth-banner-success">{done}</div> : null}

      <form onSubmit={onSubmit} noValidate>
        <label className="auth-label" htmlFor="new-password">
          New password
        </label>
        <input
          id="new-password"
          type="password"
          autoComplete="new-password"
          placeholder={`At least ${MIN_LENGTH} characters`}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="auth-input"
          required
        />
        <label className="auth-label" htmlFor="confirm-password">
          Confirm new password
        </label>
        <input
          id="confirm-password"
          type="password"
          autoComplete="new-password"
          placeholder="Type it again"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className="auth-input"
          required
        />
        <button
          type="submit"
          disabled={busy || !password || !confirm}
          className="auth-btn auth-btn-primary"
        >
          {busy ? 'Saving…' : cta}
        </button>
      </form>
    </section>
  );
}

export default function AccountPage() {
  return (
    <AuthRoute>
      {(user) => (
        <AppShell user={user}>
          <div className="account-page">
            <h1 className="account-title">Account</h1>
            <p className="account-subtitle">{user.email}</p>
            <PasswordSection />
          </div>
        </AppShell>
      )}
    </AuthRoute>
  );
}
